import { Component, EventEmitter, Input, OnInit, OnDestroy, Output, inject } from '@angular/core';
import { NgTemplateOutlet, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, Subscription } from 'rxjs';
import { WorkshopFilterService } from '../../../services/workshop-filter.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { WorkshopFilterData } from '../../../../../_shared/models';
import { Workshop, WorkshopTreeNode } from '../../../models/workshop.model';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-workshop-filter',
  standalone: true,
  imports: [NgbModule, NgTemplateOutlet, CommonModule, FormsModule, TranslocoPipe],
  templateUrl: './workshop-filter.component.html',
  styleUrl: './workshop-filter.component.scss'
})
export class WorkshopFilterComponent implements OnInit, OnDestroy {
  private workshopService = inject(WorkshopFilterService);
  private errorService = inject(ErrorHandlerService);

  @Input() disabled: boolean = false;
  @Input() filter: WorkshopFilterData = { workshops: [] };
  @Input() accountFilterChanged$: Observable<number[]>;
  @Output() filterChanged: EventEmitter<WorkshopFilterData> = new EventEmitter<WorkshopFilterData>();

  isLoading: boolean = true;
  searchTerm: string = '';

  // Tree data
  workshopTree: WorkshopTreeNode[] = [];
  filteredTree: WorkshopTreeNode[] = [];
  selectedItems: WorkshopTreeNode[] = [];
  selectedItemsCount: number = 0;

  private accountFilterSubscription: Subscription;
  private cancelSubject: Subject<void> = new Subject<void>();
  selectedAccounts: Array<number> = [];

  ngOnInit(): void {
    if (this.accountFilterChanged$) {
      this.accountFilterSubscription = this.accountFilterChanged$.subscribe(
        (accountIds) => {
          this.selectedAccounts = accountIds ?? [];
          this.selectedItems = [];
          this.filter.workshops = [];
          this.updateSelectedCount();
          this.loadWorkshopData();
        }
      );
    }
  }

  ngOnDestroy(): void {
    if (this.accountFilterSubscription) {
      this.accountFilterSubscription.unsubscribe();
    }
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async loadWorkshopData(): Promise<void> {
    this.isLoading = true;
    try {
      this.cancelSubject.next();
      const response = await this.workshopService.getWorkshopList(
        this.selectedAccounts,
        this.cancelSubject
      );
      this.workshopTree = this.buildWorkshopTree(response.data ?? []);
      this.filteredTree = this.workshopTree;
      this.applyFilter(response.data ?? []);
      this.isLoading = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  buildWorkshopTree(data: Workshop[]): WorkshopTreeNode[] {
    const countryMap = new Map<string, WorkshopTreeNode>();
    let autoId = 1;

    data.forEach((item) => {
      const country = item.country?.trim() || 'All';
      const workshopName = item.name?.trim();

      if (!workshopName) return;

      if (!countryMap.has(country)) {
        countryMap.set(country, {
          id: autoId++,
          name: country,
          checked: false,
          expanded: false,
          children: [],
          workshopData: { id: 0, name: country, country }
        });
      }

      countryMap.get(country)!.children.push({
        id: autoId++,
        parentId: countryMap.get(country)!.id,
        name: workshopName,
        checked: false,
        expanded: false,
        children: [],
        workshopData: item
      });
    });

    return Array.from(countryMap.values());
  }

  searchWorkshop(event: Event): void {
    const searchTerm = (event.target as HTMLInputElement).value;
    if (searchTerm) {
      this.filteredTree = this.searchTreeItems(this.workshopTree, searchTerm);
    } else {
      this.filteredTree = this.workshopTree;
    }

    this.filteredTree.forEach((item) => {
      this.applyToggle(item);
    });
  }

  searchTreeItems(items: WorkshopTreeNode[], searchTerm: string): WorkshopTreeNode[] {
    return items.reduce((acc: WorkshopTreeNode[], item) => {
      const parentMatches = item.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchedChildren = this.searchTreeItems(item.children, searchTerm.toLowerCase());

      if (parentMatches || matchedChildren.length > 0) {
        acc.push({
          ...item,
          children: parentMatches ? item.children : matchedChildren
        });
      }

      return acc;
    }, []);
  }

  onCheckboxChange(item: WorkshopTreeNode): void {
    this.toggleCheckBox(item);
    this.updateFilter();
    this.updateSelectedCount();
  }

  private toggleCheckBox(item: WorkshopTreeNode): void {
    const index = this.selectedItems.findIndex((x) => x.id === item.id);

    if (index === -1) {
      this.selectedItems.push(item);
      item.checked = true;
    } else {
      this.selectedItems.splice(index, 1);
      item.checked = false;
    }

    item.children.forEach((child) => {
      this.toggleChildren(child, item.checked);
    });
  }

  private toggleChildren(item: WorkshopTreeNode, toggle: boolean): void {
    if (toggle) {
      if (!this.selectedItems.find((x) => x.id === item.id)) {
        this.selectedItems.push(item);
        item.checked = true;
      }
    } else {
      const index = this.selectedItems.findIndex((x) => x.id === item.id);
      if (index !== -1) {
        this.selectedItems.splice(index, 1);
      }
      item.checked = false;
    }

    item.children.forEach((child) => {
      this.toggleChildren(child, item.checked);
    });
  }

  private applyToggle(item: WorkshopTreeNode): void {
    item.checked = !!this.selectedItems.find((x) => x.id === item.id);
    item.children.forEach((child) => {
      this.applyToggle(child);
    });
  }

  private applyFilter(data: Workshop[]): void {
    if (this.filter.workshops && this.filter.workshops.length > 0) {
      this.selectedItems = [];
      const tempTree = this.buildWorkshopTree(data);

      this.filter.workshops.forEach((filterItem: Workshop) => {
        this.findAndSelectNode(tempTree, filterItem);
      });

      this.updateSelectedCount();
    }
  }

  private findAndSelectNode(nodes: WorkshopTreeNode[], workshop: Workshop): void {
    for (const node of nodes) {
      if (node.workshopData) {
        const matches = node.workshopData.id === workshop.id ||
          (node.workshopData.name === workshop.name && node.workshopData.country === workshop.country);

        if (matches) {
          if (!this.selectedItems.find((x) => x.id === node.id)) {
            this.selectedItems.push(node);
            node.checked = true;
          }
        }
      }

      if (node.children.length > 0) {
        this.findAndSelectNode(node.children, workshop);
      }
    }
  }

  private updateFilter(): void {
    this.filter.workshops = this.selectedItems
      .filter((item) => item.workshopData && item.workshopData.id !== 0)
      .map((item) => item.workshopData!);

    this.filterChanged.emit(this.filter);
  }

  private updateSelectedCount(): void {
    this.selectedItemsCount = this.selectedItems.filter(
      (item) => item.workshopData && item.workshopData.id !== 0
    ).length;
  }
}